import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useCheckDuplicateImeiMutation } from '../../api/purchase/PurchaseApi';
import { ImeiEntry, PurchaseRow } from '../../types/purchase';

interface PropsData {
  row: PurchaseRow;
  /** Serial numbers already typed on the other rows of this invoice. */
  usedElsewhere: string[];
  onClose: () => void;
  onSave: (imeis: ImeiEntry[], commonPrice: boolean) => void;
}

const clean = (value: string) => value.replace(/[\s-]/g, '').trim();

/**
 * One box per piece. The shop types or scans each serial number, and a clash is shown here
 * rather than as an error after they press save.
 */
const ImeiDialog = ({ row, usedElsewhere, onClose, onSave }: PropsData) => {
  const count = Number(row.quantity) || 0;
  const [entries, setEntries] = useState<ImeiEntry[]>(() => {
    const existing = row.imeis || [];
    return Array.from({ length: count }, (_, index) => existing[index] || { imeiNo: '', purchasePrice: null });
  });
  const [commonPrice, setCommonPrice] = useState(row.commonPrice !== false);
  const [inStock, setInStock] = useState<string[]>([]);
  const [checkDuplicateImei] = useCheckDuplicateImeiMutation();

  const numbers = entries.map((entry) => clean(entry.imeiNo || ''));

  /** Same number twice in this dialog, or already used on another row of this invoice. */
  const clashes = useMemo(() => {
    const seen = new Set<string>();
    const repeated = new Set<string>();
    numbers.filter(Boolean).forEach((number) => {
      if (seen.has(number)) repeated.add(number);
      seen.add(number);
    });
    usedElsewhere.forEach((number) => {
      if (seen.has(number)) repeated.add(number);
    });
    return repeated;
  }, [numbers.join('|'), usedElsewhere.join('|')]);

  useEffect(() => {
    const filled = numbers.filter(Boolean);
    if (filled.length === 0) {
      setInStock([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const response = await checkDuplicateImei(filled).unwrap();
        setInStock(response.duplicates || []);
      } catch {
        setInStock([]);
      }
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbers.join('|')]);

  const setEntry = (index: number, patch: Partial<ImeiEntry>) => {
    setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, ...entry, ...patch } : entry)));
  };

  const filledCount = numbers.filter(Boolean).length;

  /*
   * Once "Same cost for every phone" is unticked, each piece carries its own cost and
   * the row's own cost no longer applies. A box left empty - or cleared after being
   * typed in - used to be read as `Number('') || 0`, so a handset was purchased at
   * zero: the invoice total came out short, and the profit on that handset later
   * looked like the whole selling price.
   *
   * Save stays disabled until every piece has a cost above zero, and the offending
   * boxes say so rather than leaving the shop to hunt for the empty one.
   */
  const costMissing = (entry: ImeiEntry) => !commonPrice && !(Number(entry.purchasePrice) > 0);
  const missingCostCount = entries.filter(costMissing).length;

  const blocked = clashes.size > 0 || inStock.length > 0 || filledCount !== count || missingCostCount > 0;

  const handleSave = () => {
    if (blocked) return;
    onSave(
      entries.map((entry) => ({
        imeiNo: clean(entry.imeiNo),
        purchasePrice: commonPrice ? null : Number(entry.purchasePrice),
      })),
      commonPrice,
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography fontWeight={700} fontSize="1rem">
          Serial Numbers
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.label} &middot; {count} phone{count === 1 ? '' : 's'}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <FormControlLabel
          control={<Checkbox checked={commonPrice} onChange={(event) => setCommonPrice(event.target.checked)} />}
          label="Same cost for every phone"
        />
        {!commonPrice && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Enter what each phone actually cost. The line total adds them up.
          </Typography>
        )}

        {entries.map((entry, index) => {
          const number = clean(entry.imeiNo || '');
          const isClash = !!number && clashes.has(number);
          const isInStock = !!number && inStock.includes(number);
          return (
            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField
                label={`Serial Number ${index + 1}`}
                size="small"
                fullWidth
                autoFocus={index === 0}
                value={entry.imeiNo || ''}
                error={isClash || isInStock}
                helperText={isClash ? 'Entered twice on this invoice' : isInStock ? 'Already in stock' : ''}
                onChange={(event) => setEntry(index, { imeiNo: event.target.value })}
              />
              {!commonPrice && (
                <TextField
                  label="Cost"
                  size="small"
                  type="number"
                  required
                  sx={{ width: 140 }}
                  value={entry.purchasePrice ?? ''}
                  error={costMissing(entry)}
                  helperText={costMissing(entry) ? 'Required' : ''}
                  // Blank stays blank: Number('') is 0, which would read as a free handset.
                  onChange={(event) =>
                    setEntry(index, { purchasePrice: event.target.value === '' ? null : Number(event.target.value) })
                  }
                />
              )}
            </Box>
          );
        })}

        {filledCount !== count && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {filledCount} of {count} entered. One serial number per piece.
          </Alert>
        )}

        {missingCostCount > 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {missingCostCount} phone{missingCostCount === 1 ? '' : 's'} still {missingCostCount === 1 ? 'has' : 'have'} no
            cost. Enter what each one cost, or tick &ldquo;Same cost for every phone&rdquo; to use the row&rsquo;s cost.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" size="small" disabled={blocked} onClick={handleSave}>
          Save Serial Numbers
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImeiDialog;
